import React, { useEffect, useState } from "react";
import { db } from "../firebase";
import {
  collection,
  onSnapshot,
  addDoc,
  updateDoc,
  deleteDoc,
  doc,
  serverTimestamp,
  query,
  orderBy,
} from "firebase/firestore";

const UI_CURRENCY = "LKR";

const EMPTY = {
  name: "",
  price: 0,
  currency: UI_CURRENCY,
  durationDays: 30,

  // per-type limits (0 = unlimited)
  courseLimit: 0,
  productLimit: 0,
  serviceLimit: 0,

  firstSaleLevel1Amount: 0,
  firstSaleLevel2Amount: 0,
  firstSaleLevel3Amount: 0,
  renewalLevel1Amount: 0,
  renewalLevel2Amount: 0,
  renewalLevel3Amount: 0,
  developerAmount: 0,
  renewalDeveloperAmount: 0,

  description: "",
  isActive: true,
};

export default function InstructorPackages() {
  const [rows, setRows] = useState([]);
  const [editingId, setEditingId] = useState(null);
  const [form, setForm] = useState(EMPTY);
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    const q1 = query(
      collection(db, "instructor_packages"),
      orderBy("createdAt", "desc"),
    );
    const unsub = onSnapshot(q1, (snap) => {
      setRows(snap.docs.map((d) => ({ id: d.id, ...d.data() })));
    });
    return () => unsub();
  }, []);

  const reset = () => {
    setEditingId(null);
    setForm(EMPTY);
  };

  const startEdit = (r) => {
    setEditingId(r.id);
    setForm({
      name: r.name || "",
      price: Number(r.price || 0),
      currency: r.currency || UI_CURRENCY,
      durationDays: Number(r.durationDays || 30),
      courseLimit: Number(r.courseLimit || 0),
      productLimit: Number(r.productLimit || 0),
      serviceLimit: Number(r.serviceLimit || 0),
      firstSaleLevel1Amount: Number(
        r.firstSaleLevel1Amount ?? r.affiliateFirstPaymentAmount ?? 0,
      ),
      firstSaleLevel2Amount: Number(r.firstSaleLevel2Amount || 0),
      firstSaleLevel3Amount: Number(r.firstSaleLevel3Amount || 0),
      renewalLevel1Amount: Number(
        r.renewalLevel1Amount ?? r.affiliateRenewalPaymentAmount ?? 0,
      ),
      renewalLevel2Amount: Number(r.renewalLevel2Amount || 0),
      renewalLevel3Amount: Number(r.renewalLevel3Amount || 0),
      developerAmount: Number(r.developerAmount || 0),
      renewalDeveloperAmount: Number(
        r.renewalDeveloperAmount ?? r.developerAmount ?? 0,
      ),
      description: r.description || "",
      isActive: r.isActive !== false,
    });
  };

  const save = async () => {
    setSaving(true);
    try {
      const payload = {
        name: String(form.name || "").trim(),
        price: Number(form.price || 0),
        currency: String(form.currency || UI_CURRENCY).toUpperCase(),
        durationDays: Number(form.durationDays || 30),

        courseLimit: Number(form.courseLimit || 0),
        productLimit: Number(form.productLimit || 0),
        serviceLimit: Number(form.serviceLimit || 0),
        firstSaleLevel1Amount: Number(form.firstSaleLevel1Amount || 0),
        firstSaleLevel2Amount: Number(form.firstSaleLevel2Amount || 0),
        firstSaleLevel3Amount: Number(form.firstSaleLevel3Amount || 0),
        renewalLevel1Amount: Number(form.renewalLevel1Amount || 0),
        renewalLevel2Amount: Number(form.renewalLevel2Amount || 0),
        renewalLevel3Amount: Number(form.renewalLevel3Amount || 0),
        affiliateFirstPaymentAmount: Number(form.firstSaleLevel1Amount || 0),
        affiliateRenewalPaymentAmount: Number(form.renewalLevel1Amount || 0),
        developerAmount: Number(form.developerAmount || 0),
        renewalDeveloperAmount: Number(
          form.renewalDeveloperAmount ?? form.developerAmount ?? 0,
        ),

        description: String(form.description || "").trim(),
        isActive: !!form.isActive,
        updatedAt: serverTimestamp(),
      };

      if (editingId) {
        await updateDoc(doc(db, "instructor_packages", editingId), payload);
      } else {
        await addDoc(collection(db, "instructor_packages"), {
          ...payload,
          createdAt: serverTimestamp(),
        });
      }
      reset();
    } catch (e) {
      console.error(e);
      alert("Failed to save package.");
    } finally {
      setSaving(false);
    }
  };

  const remove = async (id) => {
    if (!window.confirm("Delete this package?")) return;
    try {
      await deleteDoc(doc(db, "instructor_packages", id));
    } catch (e) {
      console.error(e);
      alert("Failed to delete.");
    }
  };

  return (
    <div className="p-6 space-y-6">
      <div>
        <h1 className="text-2xl font-bold">Instructor Packages</h1>
        <p className="text-gray-600 mt-1">
          Create packages instructors must buy to activate their store and
          unlock posting limits.
        </p>
      </div>

      {/* Form */}
      <div className="bg-white rounded-2xl border shadow-sm p-5 space-y-4">
        <div className="font-semibold">
          {editingId ? "Edit package" : "New package"}
        </div>

        <div className="grid md:grid-cols-2 gap-4">
          <Field label="Name">
            <input
              className="w-full border rounded-xl px-3 py-2"
              value={form.name}
              onChange={(e) => setForm((p) => ({ ...p, name: e.target.value }))}
            />
          </Field>

          <Field label={`Price (${UI_CURRENCY})`}>
            <input
              className="w-full border rounded-xl px-3 py-2"
              type="number"
              value={form.price}
              onChange={(e) =>
                setForm((p) => ({ ...p, price: e.target.value }))
              }
            />
          </Field>

          <Field label="Duration (days)">
            <input
              className="w-full border rounded-xl px-3 py-2"
              type="number"
              value={form.durationDays}
              onChange={(e) =>
                setForm((p) => ({ ...p, durationDays: e.target.value }))
              }
            />
          </Field>

          <Field label="Active">
            <label className="inline-flex items-center gap-2">
              <input
                type="checkbox"
                checked={!!form.isActive}
                onChange={(e) =>
                  setForm((p) => ({ ...p, isActive: e.target.checked }))
                }
              />
              <span className="text-sm">Visible to instructors</span>
            </label>
          </Field>
        </div>

        <div className="grid md:grid-cols-3 gap-4">
          <Field label="Course limit (0 = unlimited)">
            <input
              className="w-full border rounded-xl px-3 py-2"
              type="number"
              value={form.courseLimit}
              onChange={(e) =>
                setForm((p) => ({ ...p, courseLimit: e.target.value }))
              }
            />
          </Field>

          <Field label="Product limit (0 = unlimited)">
            <input
              className="w-full border rounded-xl px-3 py-2"
              type="number"
              value={form.productLimit}
              onChange={(e) =>
                setForm((p) => ({ ...p, productLimit: e.target.value }))
              }
            />
          </Field>

          <Field label="Service limit (0 = unlimited)">
            <input
              className="w-full border rounded-xl px-3 py-2"
              type="number"
              value={form.serviceLimit}
              onChange={(e) =>
                setForm((p) => ({ ...p, serviceLimit: e.target.value }))
              }
            />
          </Field>
        </div>

        <div className="grid md:grid-cols-4 gap-4">
          <Field label="1st sale • Level 1">
            <input
              className="w-full border rounded-xl px-3 py-2"
              type="number"
              value={form.firstSaleLevel1Amount}
              onChange={(e) =>
                setForm((p) => ({
                  ...p,
                  firstSaleLevel1Amount: e.target.value,
                }))
              }
            />
          </Field>

          <Field label="1st sale • Level 2">
            <input
              className="w-full border rounded-xl px-3 py-2"
              type="number"
              value={form.firstSaleLevel2Amount}
              onChange={(e) =>
                setForm((p) => ({
                  ...p,
                  firstSaleLevel2Amount: e.target.value,
                }))
              }
            />
          </Field>

          <Field label="1st sale • Level 3">
            <input
              className="w-full border rounded-xl px-3 py-2"
              type="number"
              value={form.firstSaleLevel3Amount}
              onChange={(e) =>
                setForm((p) => ({
                  ...p,
                  firstSaleLevel3Amount: e.target.value,
                }))
              }
            />
          </Field>

          <Field label="1st sale • Developer">
            <input
              className="w-full border rounded-xl px-3 py-2"
              type="number"
              value={form.developerAmount}
              onChange={(e) =>
                setForm((p) => ({
                  ...p,
                  developerAmount: e.target.value,
                }))
              }
            />
          </Field>
        </div>

        <div className="grid md:grid-cols-3 gap-4">
          <Field label="Renewal • Level 1">
            <input
              className="w-full border rounded-xl px-3 py-2"
              type="number"
              value={form.renewalLevel1Amount}
              onChange={(e) =>
                setForm((p) => ({
                  ...p,
                  renewalLevel1Amount: e.target.value,
                }))
              }
            />
          </Field>

          <Field label="Renewal • Level 2">
            <input
              className="w-full border rounded-xl px-3 py-2"
              type="number"
              value={form.renewalLevel2Amount}
              onChange={(e) =>
                setForm((p) => ({
                  ...p,
                  renewalLevel2Amount: e.target.value,
                }))
              }
            />
          </Field>

          <Field label="Renewal • Level 3">
            <input
              className="w-full border rounded-xl px-3 py-2"
              type="number"
              value={form.renewalLevel3Amount}
              onChange={(e) =>
                setForm((p) => ({
                  ...p,
                  renewalLevel3Amount: e.target.value,
                }))
              }
            />
          </Field>

          <Field label="Renewal • Developer">
            <input
              className="w-full border rounded-xl px-3 py-2"
              type="number"
              value={form.renewalDeveloperAmount}
              onChange={(e) =>
                setForm((p) => ({
                  ...p,
                  renewalDeveloperAmount: e.target.value,
                }))
              }
            />
          </Field>
        </div>

        <Field label="Description">
          <textarea
            className="w-full border rounded-xl px-3 py-2 min-h-[90px]"
            value={form.description}
            onChange={(e) =>
              setForm((p) => ({ ...p, description: e.target.value }))
            }
          />
        </Field>

        <div className="flex items-center gap-2">
          <button
            onClick={save}
            disabled={saving || !form.name}
            className="px-4 py-2 rounded-xl bg-blue-600 text-white font-semibold disabled:bg-gray-200 disabled:text-gray-500"
          >
            {saving ? "Saving…" : "Save"}
          </button>

          {editingId ? (
            <button
              onClick={reset}
              className="px-4 py-2 rounded-xl border bg-white hover:bg-gray-50"
            >
              Cancel
            </button>
          ) : null}
        </div>
      </div>

      {/* List */}
      <div className="bg-white rounded-2xl border shadow-sm overflow-hidden">
        <div className="px-4 py-3 bg-gray-50 border-b font-semibold">
          Packages
        </div>
        <div className="divide-y">
          {rows.map((r) => (
            <div
              key={r.id}
              className="p-4 flex items-start justify-between gap-4"
            >
              <div className="min-w-0">
                <div className="font-semibold truncate">{r.name}</div>
                <div className="text-sm text-gray-600 mt-1">
                  {UI_CURRENCY} {Number(r.price || 0).toLocaleString("en-LK")} •{" "}
                  {r.durationDays || 30} days
                  {r.isActive === false ? " • (Inactive)" : ""}
                </div>
                <div className="text-xs text-gray-600 mt-1">
                  Limits → Course: {Number(r.courseLimit || 0) || "∞"} •
                  Product: {Number(r.productLimit || 0) || "∞"} • Service:{" "}
                  {Number(r.serviceLimit || 0) || "∞"}
                </div>
                <div className="text-xs text-gray-600 mt-1">
                  1st Sale → L1 {UI_CURRENCY}{" "}
                  {Number(
                    r.firstSaleLevel1Amount ?? r.affiliateFirstPaymentAmount ?? 0,
                  ).toLocaleString("en-LK")}{" "}
                  • L2 {UI_CURRENCY}{" "}
                  {Number(r.firstSaleLevel2Amount || 0).toLocaleString("en-LK")}{" "}
                  • L3 {UI_CURRENCY}{" "}
                  {Number(r.firstSaleLevel3Amount || 0).toLocaleString("en-LK")}{" "}
                  • Developer {UI_CURRENCY}{" "}
                  {Number(r.developerAmount || 0).toLocaleString("en-LK")}
                </div>
                <div className="text-xs text-gray-600 mt-1">
                  Renewal → L1 {UI_CURRENCY}{" "}
                  {Number(
                    r.renewalLevel1Amount ?? r.affiliateRenewalPaymentAmount ?? 0,
                  ).toLocaleString("en-LK")}{" "}
                  • L2 {UI_CURRENCY}{" "}
                  {Number(r.renewalLevel2Amount || 0).toLocaleString("en-LK")}{" "}
                  • L3 {UI_CURRENCY}{" "}
                  {Number(r.renewalLevel3Amount || 0).toLocaleString("en-LK")}{" "}
                  • Developer {UI_CURRENCY}{" "}
                  {Number(
                    r.renewalDeveloperAmount ?? r.developerAmount ?? 0,
                  ).toLocaleString("en-LK")}
                </div>
                {r.description ? (
                  <div className="text-xs text-gray-500 mt-2 line-clamp-2">
                    {r.description}
                  </div>
                ) : null}
              </div>

              <div className="flex items-center gap-2">
                <button
                  onClick={() => startEdit(r)}
                  className="px-3 py-1.5 rounded-lg border hover:bg-gray-50 text-sm"
                >
                  Edit
                </button>
                <button
                  onClick={() => remove(r.id)}
                  className="px-3 py-1.5 rounded-lg border hover:bg-gray-50 text-sm text-rose-600"
                >
                  Delete
                </button>
              </div>
            </div>
          ))}
          {rows.length === 0 ? (
            <div className="p-6 text-sm text-gray-500">No packages yet.</div>
          ) : null}
        </div>
      </div>
    </div>
  );
}

function Field({ label, children }) {
  return (
    <div>
      <div className="text-sm font-semibold">{label}</div>
      <div className="mt-1">{children}</div>
    </div>
  );
}
